import dynamic from "next/dynamic";
import { GlobalModalContext } from "@directv-digital/dds.core.global-modal-context";
import React from "react";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";

const DtvButtonModal = dynamic(
  () => import("@/video-lab/dtv-button-modal")
);

export const GlobalModal = ({ children }) => {
  try{
    const [buttonModalData, setButtonModalData] = React.useState<any>();
    const { modalType, modalData } = buttonModalData || {};
    const [openModal, setOpenModal] = React.useState(false);

    const showModal = (type: string, data: any = undefined) => {
      const isInline = data?.inline && data?.inlineAnchorId;
      // Only trigger modal open state for non-inline usage
      setOpenModal(!isInline);
      setButtonModalData({
        ...buttonModalData,
        modalType: type,
        modalData: data
      });
      if (!isInline) {
        setTimeout(() => {
          const eleId = data?.id;
          const ele = document.querySelector(`.legalModal #${eleId}`);
          if(ele) {
            ele.scrollIntoView();
          }
        }, 1000);
      }
    };

    const hideModal = () => {
      setOpenModal(false);
      setButtonModalData({
        ...buttonModalData,
        modalType: null,
        modalData: undefined
      });
    };

    const renderComponent = () => {
      try{
        if (!(modalType || modalData)) {
          return null;
        }

        const modalDataProps = {
          ...modalData,
          openModal,
          handleClose: hideModal
        };
        return <DtvButtonModal modalData={modalDataProps} modalType={modalType} />;
      } catch (error) {
        return <ErrorDisplay error={error}/>
      }
    };

    /* start-copilot */
    React.useEffect(() => {
      if (typeof window === "undefined") {
        return undefined;
      }
      const handleFusionOpenModal = (event: Event) => {
        try {
          const customEvent = event as CustomEvent<{ modalType?: string; modalData?: any }>;
          const incomingType = customEvent?.detail?.modalType;
          if (!incomingType) {
            return;
          }
          showModal(incomingType, customEvent.detail?.modalData);
        } catch (error) {
          console.error("Failed to handle fusion:open-modal event", error);
        }
      };

      window.addEventListener("fusion:open-modal", handleFusionOpenModal as EventListener);
      return () => window.removeEventListener("fusion:open-modal", handleFusionOpenModal as EventListener);
    }, []);
    /* end-copilot */

    // Warm the modal chunks while the main thread is idle. Without this, the
    // first "See details" tap pays the dynamic-import fetch + evaluate inside
    // the tap's frame (INP audit FP-04 projection: ~184ms script eval @4x
    // throttle — mechanism-derived, not field-measured; validate via RUM
    // post-release).
    React.useEffect(() => {
      // useEffect never runs during SSR, so no window guard is needed here —
      // only the Save-Data opt-out.
      if ((navigator as any)?.connection?.saveData) {
        return undefined;
      }
      // Warms the legal ("See details") path only — mdu/iframe modals are
      // rare enough to keep paying first-open cost. A failed prefetch must
      // stay QUIET (no throw, no RUM noise): the real tap re-imports through
      // the normal path, which already has ErrorDisplay handling. debug-level
      // log keeps a post-deploy hashed-chunk 404 diagnosable from a console
      // (DevTools hides the "Verbose" level by default — enable it to see this).
      const logPrefetchMiss = (e: unknown) =>
        console.debug("[GlobalModal] modal chunk prefetch failed", e);
      const prefetchModalChunks = () => {
        import("@/video-lab/dtv-button-modal").catch(logPrefetchMiss);
        import("@directv-digital/dds.ui.dtv-legal-modal").catch(logPrefetchMiss);
      };
      if (typeof (window as any).requestIdleCallback === "function") {
        // timeout bounds the warmup on busy pages (idle may never come before the
        // first tap) and keeps parity with the 3s setTimeout fallback below.
        const idleId = (window as any).requestIdleCallback(prefetchModalChunks, { timeout: 3000 });
        // Optional-chained: a partial polyfill that ships requestIdleCallback
        // without cancelIdleCallback must not throw during unmount cleanup.
        return () => (window as any).cancelIdleCallback?.(idleId);
      }
      const timerId = setTimeout(prefetchModalChunks, 3000);
      return () => clearTimeout(timerId);
    }, []);

    return (
      <GlobalModalContext.Provider value={{
 showModal,
hideModal
}}>
        {renderComponent()}
        {children}
      </GlobalModalContext.Provider>
    );
  } catch (error) {
    return <ErrorDisplay component= "Global Modal" error={error}  />
  }
};
